import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useAnnouncementTracking } from '../hooks/useAnnouncementTracking';
import type { AnnouncementConfig } from '../types';

interface WhatsNewModalContentProps {
  announcement: AnnouncementConfig;
  onDismiss: () => void;
}

/**
 * Renders the content of a single announcement within the What's New modal.
 */
export function WhatsNewModalContent({ announcement, onDismiss }: WhatsNewModalContentProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const { trackCtaClick } = useAnnouncementTracking(announcement.id);
  const { content } = announcement;

  const handlePrimaryCta = () => {
    trackCtaClick('primary');
    onDismiss();

    if (content.cta?.to) {
      navigate(content.cta.to);
    } else if (content.cta?.href) {
      window.open(content.cta.href, '_blank', 'noopener,noreferrer');
    } else if (content.cta?.onClick) {
      content.cta.onClick();
    }
  };

  const handleSecondaryCta = () => {
    trackCtaClick('secondary');

    if (content.secondaryCta?.to) {
      navigate(content.secondaryCta.to);
    } else if (content.secondaryCta?.href) {
      window.open(content.secondaryCta.href, '_blank', 'noopener,noreferrer');
    } else if (content.secondaryCta?.onClick) {
      content.secondaryCta.onClick();
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-8 lg:min-h-[350px] lg:flex-row">
      {/* Left content */}
      <div className="flex flex-1 flex-col justify-center">
        <div className="mb-6 inline-block">
          <span className="rounded-full bg-lime/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-lime">
            {content.variant === 'feature'
              ? 'New Feature'
              : content.variant === 'update'
                ? 'Update'
                : 'Announcement'}
          </span>
        </div>

        <h1 className="mb-4 bg-gradient-to-br from-white to-white/80 bg-clip-text text-3xl font-black leading-tight text-transparent sm:text-4xl lg:text-5xl">
          {content.title}
        </h1>

        <p className="mb-6 text-lg leading-relaxed text-white/70">
          {content.description}
        </p>

        {content.body && (
          <div className="mb-8 space-y-4">
            {content.body.split('\n').map((line, i) => {
              const trimmed = line.trim();
              if (!trimmed) return null;

              // Check if it's a list item
              const isListItem = trimmed.startsWith('- ') || trimmed.startsWith('* ');
              const cleanText = isListItem ? trimmed.substring(2) : trimmed;

              // Handle markdown-style bold
              const parts = cleanText.split(/\*\*(.*?)\*\*/g);
              const renderedText = parts.map((part, j) =>
                j % 2 === 1 ? (
                  <span key={j} className="font-semibold text-white/90">
                    {part}
                  </span>
                ) : (
                  part
                )
              );

              return (
                <div key={i} className="flex gap-3">
                  {isListItem ? (
                    <div className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-lime/20 shadow-[0_0_10px_rgba(188,215,82,0.2)]">
                      <svg
                        className="h-2.5 w-2.5 text-lime"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={4}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="h-0 w-4 shrink-0" />
                  )}
                  <p className="text-[15px] leading-relaxed text-white/60">{renderedText}</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-3">
          {content.cta && (
            <Button
              onClick={handlePrimaryCta}
              className="group bg-lime px-6 text-black hover:bg-lime/90"
            >
              {content.cta.label}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
          {content.secondaryCta && (
            <Button
              variant="outline"
              onClick={handleSecondaryCta}
              className="border-white/20 px-6 text-white hover:bg-white/10"
            >
              {content.secondaryCta.label}
            </Button>
          )}
        </div>
      </div>

      {/* Right image/logo area */}
      <div className="relative hidden w-[45%] items-center justify-center lg:flex">
        {content.graphics && content.graphics.length > 0 ? (
          <div className="relative w-full px-4">
            {/* Ambient background glows */}
            <div className="absolute -left-10 top-0 h-32 w-32 rounded-full bg-lime/10 blur-[60px]" />
            <div className="absolute -right-10 bottom-0 h-32 w-32 rounded-full bg-lime/5 blur-[60px]" />

            <div className="flex flex-col">
              {content.graphics.map((src, idx) => {
                const rotations = [-3, 2, -1.5];
                const translations = [0, -24, -48];
                const horizontalOffsets = ['self-start', 'ml-8 self-center', 'ml-16 self-end'];
                const isHovered = hoveredIndex === idx;
                const isAnyHovered = hoveredIndex !== null;
                
                return (
                  <div
                    key={src}
                    onMouseEnter={() => setHoveredIndex(idx)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`group relative rounded-xl border border-white/10 bg-[#0A0F0C]/80 p-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-700 ease-in-out
                      ${horizontalOffsets[idx] || 'self-center'}
                      ${isHovered ? 'scale-110 border-lime/40 shadow-[0_40px_80px_rgba(188,215,82,0.15)] z-50' : isAnyHovered ? 'opacity-40 scale-95 blur-[1px]' : ''}
                    `}
                    style={{ 
                      zIndex: isHovered ? 50 : 10 + idx,
                      transform: isHovered 
                        ? 'rotate(0deg) translateY(-12px)' 
                        : isAnyHovered 
                          ? `rotate(${rotations[idx] * 0.5}deg) translateY(${idx < (hoveredIndex ?? 0) ? -20 : 20}px)`
                          : `rotate(${rotations[idx] || 0}deg)`,
                      marginTop: idx === 0 ? 0 : translations[idx-1] || -32
                    }}
                  >
                    {/* Subtle glow instead of intense burst */}
                    <div className={`absolute inset-0 -z-10 rounded-lg bg-lime/10 blur-2xl transition-opacity duration-700 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />

                    <img
                      src={src}
                      alt=""
                      className="h-auto w-full rounded-lg"
                      loading="lazy"
                    />

                    {/* Premium glass shimmer effect */}
                    <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
                      <div className={`absolute -inset-[100%] aspect-square bg-gradient-to-tr from-transparent via-white/[0.05] to-transparent transition-transform duration-[1500ms] ${isHovered ? 'translate-x-full translate-y-full' : ''}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : content.image ? (
          <div className="relative">
            <div className="absolute inset-0 -z-10 rounded-2xl bg-lime/10 blur-2xl" />
            <img
              src={content.image}
              alt={content.title}
              className="h-auto max-h-[320px] w-auto max-w-full rounded-xl border border-lime/20 shadow-2xl"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center opacity-30">
            <svg
              className="h-40 w-40 text-lime"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
